import React, { useEffect, useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, ShoppingBag, ChevronDown, User, LogOut, Menu, Layers, 
  ShoppingCart,
  Images
} from 'lucide-react';

const DashboardLayout = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [userData, setUserData] = useState(null);
  
  // Separate states for different menus
  const [isProductMenuOpen, setProductMenuOpen] = useState(false);
  const [isCategoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [isOrdersMenuOpen, setOrdersMenuOpen] = useState(false);
  
  const location = useLocation();
  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    window.location.href = '/admin'; // Redirect and refresh
  };


  useEffect(() => {
    const storedUser = localStorage.getItem("admin_user");

    if (storedUser) {
      setUserData(JSON.parse(storedUser));
    }
  }, []);


  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      
      {/* --- SIDEBAR --- */}
      <aside className={`fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-100 transition-all duration-300 ${isSidebarOpen ? "w-72" : "w-20"}`}>
        
        {/* TOP: USER PROFILE SECTION */}
        <div className="p-6 border-b border-gray-50 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-900 flex items-center justify-center text-[#ffaa00] shadow-lg flex-shrink-0">
              <User size={22} />
            </div>
            {isSidebarOpen && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <p className="text-sm font-black text-gray-900 leading-none">{userData?.name}</p>
                <p className="text-[10px] font-bold text-[#8A8A89] uppercase mt-1">Super Admin</p>
              </motion.div>
            )}
          </div>
        </div>

        <nav className="p-4 space-y-2">
          {/* Dashboard Home Link */}
          <Link to="/admin/dashboard" className={`flex items-center p-3 rounded-xl transition-all ${isActive('/admin/dashboard') ? "bg-orange-50 text-[#ffaa00]" : "text-gray-500 hover:bg-gray-50"}`}>
            <LayoutDashboard size={20} />
            {isSidebarOpen && <span className="ml-3 font-bold text-sm">Vue d'ensemble</span>}
          </Link>

          {/* PRODUCTS GROUP */}
          <div className="space-y-1">
            <div 
              onClick={() => isSidebarOpen && setProductMenuOpen(!isProductMenuOpen)}
              className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all
              ${location.pathname.includes('/products') ? "text-[#ffaa00]" : "text-gray-500 hover:bg-gray-50"}`}
            >
              <div className="flex items-center">
                <ShoppingBag size={20} />
                {isSidebarOpen && <span className="ml-3 font-bold text-sm">Produits</span>}
              </div>
              {isSidebarOpen && <ChevronDown size={14} className={`transition-transform ${isProductMenuOpen ? "rotate-180" : ""}`} />}
            </div>

            <AnimatePresence>
              {isSidebarOpen && isProductMenuOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="ml-9 flex flex-col space-y-1 border-l-2 border-gray-50">
                  <Link to="/admin/dashboard/products" className={`p-2 text-xs font-bold rounded-lg pl-4 ${isActive('/admin/dashboard/products') ? "text-[#ffaa00]" : "text-gray-400 hover:text-gray-900"}`}>
                    Tous les Produits
                  </Link>
                  <Link to="/admin/dashboard/add-product" className={`p-2 text-xs font-bold rounded-lg pl-4 ${isActive('/admin/dashboard/add-product') ? "text-[#ffaa00]" : "text-gray-400 hover:text-gray-900"}`}>
                    Ajouter Nouveau
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* CATEGORIES GROUP */}
          <div className="space-y-1">
            <div 
              onClick={() => isSidebarOpen && setCategoryMenuOpen(!isCategoryMenuOpen)}
              className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all
              ${location.pathname.includes('/categories') ? "text-[#ffaa00]" : "text-gray-500 hover:bg-gray-50"}`}
            >
              <div className="flex items-center">
                <Layers size={20} />
                {isSidebarOpen && <span className="ml-3 font-bold text-sm">Catégories</span>}
              </div>
              {isSidebarOpen && <ChevronDown size={14} className={`transition-transform ${isCategoryMenuOpen ? "rotate-180" : ""}`} />}
            </div>

            <AnimatePresence>
              {isSidebarOpen && isCategoryMenuOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="ml-9 flex flex-col space-y-1 border-l-2 border-gray-50">
                  <Link to="/admin/dashboard/categories" className={`p-2 text-xs font-bold rounded-lg pl-4 ${isActive('/admin/dashboard/categories') ? "text-[#ffaa00]" : "text-gray-400 hover:text-gray-900"}`}>
                    Toutes les Catégories
                  </Link>
                  <Link to="/admin/dashboard/add-category" className={`p-2 text-xs font-bold rounded-lg pl-4 ${isActive('/admin/dashboard/add-category') ? "text-[#ffaa00]" : "text-gray-400 hover:text-gray-900"}`}>
                    Ajouter Catégorie
                  </Link>
                  <Link to="/admin/dashboard/sous-category" className={`p-2 text-xs font-bold rounded-lg pl-4 ${isActive('/admin/dashboard/sous-category') ? "text-[#ffaa00]" : "text-gray-400 hover:text-gray-900"}`}>
                    Tous les Sous Catégories
                  </Link>
                  <Link to="/admin/dashboard/add-sous-category" className={`p-2 text-xs font-bold rounded-lg pl-4 ${isActive('/admin/dashboard/add-sous-category') ? "text-[#ffaa00]" : "text-gray-400 hover:text-gray-900"}`}>
                    Ajouter Sous Catégorie
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>

            
          </div>





          {/* ORDERS GROUP */}
            <div className="space-y-1">
            <div 
              onClick={() => isSidebarOpen && setOrdersMenuOpen(!isOrdersMenuOpen)}
              className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all
              ${location.pathname.includes('/orders') ? "text-[#ffaa00]" : "text-gray-500 hover:bg-gray-50"}`}
            >
              <div className="flex items-center">
                <ShoppingCart size={20} />
                {isSidebarOpen && <span className="ml-3 font-bold text-sm">Commandes</span>}
              </div>
              {isSidebarOpen && <ChevronDown size={14} className={`transition-transform ${isOrdersMenuOpen ? "rotate-180" : ""}`} />}
            </div>

            <AnimatePresence>
              {isSidebarOpen && isOrdersMenuOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="ml-9 flex flex-col space-y-1 border-l-2 border-gray-50">
                  <Link to="/admin/dashboard/orders" className={`p-2 text-xs font-bold rounded-lg pl-4 ${isActive('/admin/dashboard/orders') ? "text-[#ffaa00]" : "text-gray-400 hover:text-gray-900"}`}>
                    Toutes les Commandes
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>


            
          </div>





          {/* Dashboard Home Link */}
          <Link to="/admin/dashboard/sliders" className={`flex items-center p-3 rounded-xl transition-all ${isActive('/admin/dashboard/sliders') ? "bg-orange-50 text-[#ffaa00]" : "text-gray-500 hover:bg-gray-50"}`}>
            <Images size={20} />
            {isSidebarOpen && <span className="ml-3 font-bold text-sm">Les Sliders</span>}
          </Link>






        </nav>

        {/* LOGOUT */}
        <div className="absolute bottom-6 w-full px-4 text-center">
          <button className="w-full flex items-center p-3 rounded-xl text-red-500 hover:bg-red-50 transition-all font-bold text-sm">
            <LogOut size={20} />
            {isSidebarOpen && <Link onClick={() => handleLogout()}><span className="ml-3">Déconnexion</span></Link>}
          </button>
        </div>
      </aside>

      {/* --- CONTENT AREA --- */}
      <main className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarOpen ? "ml-72" : "ml-20"}`}>
        <header className="h-20 bg-white/80 backdrop-blur-md border-b border-gray-100 flex items-center px-8 sticky top-0 z-40">
            <button onClick={() => setSidebarOpen(!isSidebarOpen)} className="p-2 hover:bg-gray-50 rounded-lg transition-colors">
                <Menu size={20} />
            </button>
            <div className="ml-auto flex items-center gap-4">
              <Link target='_blank' to="/" className="text-[10px] font-black uppercase tracking-widest text-[#8A8A89] hover:text-[#ffaa00]">
                Voir le site
              </Link>
            </div>
        </header>

        <div className="p-8">
            <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;